import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { fetchAutocompleteSuggestions } from "Utilities/Address/AddressValidation/autocomplete";

import { deriveBaseStreet } from "../logic/secondaryUnit";

// Cheap "looks like an address" pre-gate (a number + a word) so we don't pay a metered Smarty
// call on an empty or single-token field; deliberately permissive — Smarty filters out the rest.
// The gate is TWO conditions: at least one digit ANYWHERE, and at least two tokens. The digit's
// position is irrelevant, because the primary number is not always first:
//   - house-number-first streets ("800 Main St")
//   - letter-prefixed rural grid numbers ("N4867 810th St", "N112W17501 Mequon Rd")
//   - number-LAST forms where the leading token is a keyword: "PO Box 1", "RR 2 Box 15",
//     "HC 1 Box 5", "PSC 1234 Box 5". Requiring the digit in the first token silently skipped
//     Smarty for every one of these, leaving them un-verifiable even though they are deliverable
//     USPS addresses (a PO Box is exactly what a resident of a PO-Box-only ZIP must use).
// `(?=.*\d)` is the digit-anywhere lookahead; `\S+\s+\S` requires a second token.
const STREET_REGEX = /^(?=.*\d)\s*\S+\s+\S/;
// 300ms balances responsive shadow-text against not burning a Smarty call
// per keystroke (each is metered). Tunable; tests wait on settled state
// (e.g. shadow text appearing) rather than asserting this constant.
const DEBOUNCE_MS = 300;
// react-query key namespace for autocomplete results. Shared across hook
// instances so two address fields on the same page hit cache for the same
// prefix instead of each paying a Smarty call.
const QUERY_KEY = "smartyAutocomplete";

const EMPTY_CANDIDATES = [];

// --- Debounced fetch keyed on the street field -------------------------
//
// Two-step pipeline:
//   1. Typing updates `value.addr1` immediately; this effect debounces
//      that into `debouncedAddr1` after DEBOUNCE_MS of quiet.
//   2. `useQuery` below keys on `debouncedAddr1` — when it changes, a
//      new query fires. react-query handles cancellation (signal), the
//      cache (a re-typed prefix hits cache), and cross-instance dedupe.
export default function useSmartyCandidates({
  addr1,
  city,
  country,
  canFetch,
  cityEditedRef,
}) {
  const [debouncedAddr1, setDebouncedAddr1] = useState(addr1);
  // Rides the same debounce as the street and is composed into Smarty's `search` (street + city),
  // so a USPS-mailable ALTERNATE city verifies (the street alone only returns the preferred-city
  // row). It does not gate the fetch — street validity does.
  const [debouncedCity, setDebouncedCity] = useState(city);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!canFetch) {
      // Snap the key to "" so a previously-cached prefix doesn't keep surfacing candidates
      // after the gate closes (verified, skipped, typed back below the regex).
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setDebouncedAddr1("");
      setDebouncedCity("");
      return undefined;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedAddr1(addr1);
      // Only a user-TYPED city is sent (cityEditedRef): composing a stale prefilled city would
      // break the street query — Smarty has no "<street> <stale-city>" completion.
      setDebouncedCity(cityEditedRef.current ? city : "");
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [addr1, city, canFetch, cityEditedRef]);

  const queryEnabled = canFetch && STREET_REGEX.test(debouncedAddr1);
  const { data: queryData, isFetching, isError } = useQuery({
    queryKey: [QUERY_KEY, country, debouncedAddr1, debouncedCity],
    queryFn: ({ signal }) =>
      fetchAutocompleteSuggestions(debouncedAddr1, country, {
        signal,
        city: debouncedCity,
      }),
    enabled: queryEnabled,
    // Hold the prior prefix's candidates while the next prefix fetches; without it react-query
    // returns data:undefined during the round-trip, collapsing allCandidates → topCandidate null
    // → the shadow text blinks off and back. (isFetching stays true, so the defer logic is unaffected.)
    keepPreviousData: true,
    // Suggestions for a prefix don't change within a session, so never refetch or retry — the next
    // keystroke is a better recovery than a retry on a metered call.
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const allCandidates = canFetch
    ? Array.isArray(queryData)
      ? queryData
      : EMPTY_CANDIDATES
    : EMPTY_CANDIDATES;

  // Base-street fallback for a pasted/fast-typed "<street> <bad unit>". Incremental typing queries
  // (and retains) the bare street before the unit is appended, but a whole-string paste collapses
  // into one query that comes back empty — nothing retained, autocomplete dies though the street is
  // real. When the primary settles empty, fire ONE query for the derived base street into the same
  // retained pool. Shares QUERY_KEY, so a prefix already typed hits cache instead of a new call.
  const primarySettledEmpty =
    queryEnabled && !isFetching && allCandidates.length === 0;
  const baseStreet = primarySettledEmpty
    ? deriveBaseStreet(debouncedAddr1)
    : null;
  const { data: baseQueryData, isError: baseQueryErrored } = useQuery({
    queryKey: [QUERY_KEY, country, baseStreet, debouncedCity],
    queryFn: ({ signal }) =>
      fetchAutocompleteSuggestions(baseStreet, country, {
        signal,
        city: debouncedCity,
      }),
    enabled: canFetch && Boolean(baseStreet),
    // No keepPreviousData here (unlike the primary query): retainedCandidates already falls back to
    // lastNonEmptyCandidatesRef, which bridges any gap while this fallback refetches.
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const baseFallbackCandidates = Array.isArray(baseQueryData)
    ? baseQueryData
    : EMPTY_CANDIDATES;

  // The base-street fallback is expected (the primary settled empty on a
  // "<street> <unit>"-shaped value) but its query hasn't landed yet —
  // `baseQueryData` is undefined until that second round-trip resolves (this
  // covers both the about-to-fire frame and the in-flight window, with no
  // one-render gap that an `isFetching` term would leave). It IS in-flight too —
  // see `fetchInFlight` below. Drop the wait if the fallback ERRORED (data stays
  // undefined on a thrown query): a failed fallback degrades to a plain
  // unverifiable rather than hanging the verdict forever.
  const baseFallbackPending =
    Boolean(baseStreet) && baseQueryData === undefined && !baseQueryErrored;

  // "A resolve right now would see stale data." True between a keystroke and the
  // candidates settling: debounce pending, the primary query fetching, OR the
  // base-street fallback still pending. The fallback term is load-bearing —
  // without it `fetchInFlight` dropped to false the instant the primary settled
  // empty, so a group-blur that fired then re-resolved against an empty pool and
  // settled a bare unverifiable, even though the fallback was about to retain the
  // real base (the "type the whole string + leave immediately" bug). Used by
  // resolveOnGroupBlur to defer the auto-resolve until the candidates land.
  const fetchInFlight =
    canFetch && (debouncedAddr1 !== addr1 || isFetching || baseFallbackPending);

  // The pool base-retain reads: prefer live candidates, else the base-street fallback, else the
  // last non-empty set so the ghost doesn't blink off between keystrokes. Reactive (not just the
  // ref) so pickTopCandidate recomputes the moment the fallback lands — a ref mutation wouldn't.
  const lastNonEmptyCandidatesRef = useRef(EMPTY_CANDIDATES);
  const retainedCandidates = allCandidates.length
    ? allCandidates
    : baseFallbackCandidates.length
      ? baseFallbackCandidates
      : lastNonEmptyCandidatesRef.current;
  useEffect(() => {
    lastNonEmptyCandidatesRef.current = retainedCandidates;
  }, [retainedCandidates]);

  // The primary fetch threw (4xx/5xx/network/parse) rather than returning a
  // result — a Smarty outage. Gated on `queryEnabled` so a stale error from a
  // disabled query (e.g. the street was cleared) doesn't keep the flow off; it
  // reflects only the live, enabled query. The engine treats this like the
  // non-US/breaker OFF state for the commit + callout paths (but NOT canFetch:
  // an outage is transient, so the next keystroke must keep trying and recover).
  const fetchErrored = queryEnabled && isError;

  return {
    allCandidates,
    fetchInFlight,
    fetchErrored,
    lastNonEmptyCandidatesRef,
    retainedCandidates,
  };
}

export { STREET_REGEX, DEBOUNCE_MS };
